// Verified finance formulas — illustrative only, no external data.

export const fmtINR = (n) => {
  if (!isFinite(n)) return "₹0";
  const v = Math.round(n);
  return "₹" + v.toLocaleString("en-IN");
};

export const fmtCompact = (n) => {
  if (!isFinite(n)) return "₹0";
  if (n >= 10000000) return "₹" + (n / 10000000).toFixed(2) + " Cr";
  if (n >= 100000) return "₹" + (n / 100000).toFixed(2) + " L";
  return fmtINR(n);
};

// SIP future value (monthly annuity-due)
export function sipFutureValue(P, annualRate, years) {
  const i = annualRate / 1200;
  const n = years * 12;
  if (i === 0) return { fv: P * n, invested: P * n };
  const fv = P * ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
  return { fv, invested: P * n };
}

// Step-up SIP: month-by-month, amount steps up by stepPct at each year boundary
export function stepUpSip(P, annualRate, years, stepPct) {
  const i = annualRate / 1200;
  let corpus = 0;
  let amount = P;
  let invested = 0;
  const series = [];
  for (let m = 1; m <= years * 12; m++) {
    corpus = (corpus + amount) * (1 + i);
    invested += amount;
    if (m % 12 === 0) {
      series.push({ year: m / 12, value: Math.round(corpus), invested: Math.round(invested) });
      amount = amount * (1 + stepPct / 100);
    }
  }
  return { fv: corpus, invested, series };
}

// Lump sum: annual compounding
export function lumpsumFutureValue(P, annualRate, years) {
  const fv = P * Math.pow(1 + annualRate / 100, years);
  const series = [];
  for (let y = 1; y <= years; y++) {
    series.push({ year: y, value: Math.round(P * Math.pow(1 + annualRate / 100, y)), invested: P });
  }
  return { fv, invested: P, series };
}

// SWP simulation: corpus grows monthly, withdrawal each month
export function swpSimulate(corpusStart, annualRate, withdrawal, years) {
  const i = annualRate / 1200;
  let corpus = corpusStart;
  let totalWithdrawn = 0;
  let depletionMonth = null;
  const series = [];
  for (let m = 1; m <= years * 12; m++) {
    corpus = corpus * (1 + i) - withdrawal;
    if (corpus <= 0 && depletionMonth === null) {
      depletionMonth = m;
      corpus = 0;
    }
    totalWithdrawn += corpus > 0 || depletionMonth === m ? withdrawal : 0;
    if (m % 12 === 0) series.push({ year: m / 12, value: Math.max(0, Math.round(corpus)) });
  }
  return { finalCorpus: Math.max(0, corpus), totalWithdrawn, depletionMonth, series };
}

// SIP + SWP: accumulate via SIP for accumYears, then withdraw for swpYears
export function sipThenSwp(P, annualRate, accumYears, withdrawal, swpYears) {
  const { fv } = sipFutureValue(P, annualRate, accumYears);
  const swp = swpSimulate(fv, annualRate, withdrawal, swpYears);
  return { corpusAtRetirement: fv, ...swp };
}

// Retirement planning
export function retirementPlan({
  currentAge,
  retireAge,
  lifeExpectancy,
  monthlyExpense,
  inflation,
  preReturn,
  postReturn,
  currentInvestments,
}) {
  const yearsToRetire = retireAge - currentAge;
  const withdrawalYears = lifeExpectancy - retireAge;
  // Inflate monthly expense to retirement
  const futureMonthly = monthlyExpense * Math.pow(1 + inflation / 100, yearsToRetire);
  const annualExpenseAtRetire = futureMonthly * 12;
  // Real return during withdrawal phase
  const realReturn = (1 + postReturn / 100) / (1 + inflation / 100) - 1;
  // Growing annuity present value at retirement
  let requiredCorpus;
  if (Math.abs(realReturn) < 1e-9) {
    requiredCorpus = annualExpenseAtRetire * withdrawalYears;
  } else {
    requiredCorpus =
      (annualExpenseAtRetire / realReturn) *
      (1 - Math.pow((1 + inflation / 100) / (1 + postReturn / 100), withdrawalYears)) *
      (1 + realReturn);
  }
  // Future value of existing investments
  const fvExisting = currentInvestments * Math.pow(1 + preReturn / 100, yearsToRetire);
  const shortfall = Math.max(0, requiredCorpus - fvExisting);
  // Required monthly SIP to close shortfall
  const i = preReturn / 1200;
  const n = yearsToRetire * 12;
  let requiredSip = 0;
  if (shortfall > 0 && n > 0) {
    requiredSip = shortfall / (((Math.pow(1 + i, n) - 1) / i) * (1 + i));
  }
  return { futureMonthly, requiredCorpus, fvExisting, shortfall, requiredSip, yearsToRetire, withdrawalYears };
}

// Bond current yield & approximate YTM
export function bondMetrics({ face, price, couponRate, years }) {
  const annualCoupon = (couponRate / 100) * face;
  const currentYield = (annualCoupon / price) * 100;
  const ytm =
    ((annualCoupon + (face - price) / years) / ((face + price) / 2)) * 100;
  return { annualCoupon, currentYield, ytm };
}

// Goal plan: inflate today's cost to the goal date, credit existing savings,
// and solve for the flat monthly SIP and the starting SIP of a step-up plan.
export function goalPlan({ cost, years, inflation, annualReturn, existing = 0, stepPct = 10 }) {
  const futureCost = cost * Math.pow(1 + inflation / 100, years);
  const fvExisting = existing * Math.pow(1 + annualReturn / 100, years);
  const gap = Math.max(0, futureCost - fvExisting);
  const unit = sipFutureValue(1, annualReturn, years).fv;
  const sip = years > 0 ? gap / unit : gap;
  const stepUnit = stepUpSip(1, annualReturn, years, stepPct).fv;
  const stepSip = years > 0 ? gap / stepUnit : gap;
  const series = [];
  for (let y = 1; y <= years; y++) {
    const { fv, invested } = sipFutureValue(sip, annualReturn, y);
    series.push({
      year: y,
      value: Math.round(fv + existing * Math.pow(1 + annualReturn / 100, y)),
      invested: Math.round(invested + existing),
      target: Math.round(cost * Math.pow(1 + inflation / 100, y)),
    });
  }
  return { futureCost, fvExisting, gap, sip, stepSip, invested: sip * years * 12 + existing, series };
}

// Cost of waiting: SIP needed to reach the same future amount if you start `delay` years later.
export function costOfDelay({ target, years, annualReturn, delays = [0, 1, 3, 5] }) {
  return delays
    .filter((d) => d < years)
    .map((d) => {
      const sip = target / sipFutureValue(1, annualReturn, years - d).fv;
      return { delay: d, sip, invested: sip * (years - d) * 12 };
    });
}

// Months until a step-up SIP plus existing savings reaches `target` (capped at 50 years).
export function timeToTarget({ target, sip, annualReturn, stepPct = 0, existing = 0 }) {
  const i = annualReturn / 1200;
  let corpus = existing;
  let amount = sip;
  let invested = existing;
  const series = [];
  for (let m = 1; m <= 600; m++) {
    corpus = (corpus + amount) * (1 + i);
    invested += amount;
    if (m % 12 === 0) {
      series.push({ year: m / 12, value: Math.round(corpus), invested: Math.round(invested) });
      amount *= 1 + stepPct / 100;
    }
    if (corpus >= target) {
      if (m % 12 !== 0) series.push({ year: +(m / 12).toFixed(1), value: Math.round(corpus), invested: Math.round(invested) });
      return { months: m, invested, corpus, series };
    }
  }
  return { months: null, invested, corpus, series };
}

// Illustrative glide path: equity share by years left to the goal.
export function glideAllocation(yearsLeft) {
  if (yearsLeft >= 10) return { equity: 70, debt: 20, gold: 10 };
  if (yearsLeft >= 7) return { equity: 60, debt: 30, gold: 10 };
  if (yearsLeft >= 5) return { equity: 50, debt: 40, gold: 10 };
  if (yearsLeft >= 3) return { equity: 30, debt: 60, gold: 10 };
  return { equity: 10, debt: 80, gold: 10 };
}

// Home-loan EMI (reducing balance, monthly).
export function emi(principal, annualRate, years) {
  const i = annualRate / 1200;
  const n = years * 12;
  if (n <= 0) return 0;
  if (i === 0) return principal / n;
  return (principal * i * Math.pow(1 + i, n)) / (Math.pow(1 + i, n) - 1);
}

// Section 80C via ELSS (old tax regime only): deduction capped at ₹1.5 lakh,
// tax saved = deduction × slab × (1 + 4% cess). Corpus assumes the yearly amount
// is invested monthly as a SIP.
export function elssTaxPlan({ annual, slabPct, years, annualReturn }) {
  const deduction = Math.min(annual, 150000);
  const taxSavedYear = deduction * (slabPct / 100) * 1.04;
  const { fv, invested } = sipFutureValue(annual / 12, annualReturn, years);
  const series = [];
  for (let y = 1; y <= years; y++) {
    const s = sipFutureValue(annual / 12, annualReturn, y);
    series.push({ year: y, value: Math.round(s.fv), invested: Math.round(s.invested) });
  }
  return { deduction, taxSavedYear, taxSavedTotal: taxSavedYear * years, fv, invested, series };
}

// Emergency fund: target = months × expenses; months to close the gap at a monthly saving.
export function emergencyFund({ monthlyExpense, months, existing, monthlySaving }) {
  const target = monthlyExpense * months;
  const gap = Math.max(0, target - existing);
  const monthsToBuild = gap === 0 ? 0 : monthlySaving > 0 ? Math.ceil(gap / monthlySaving) : null;
  return { target, gap, monthsToBuild };
}

// SWP where the monthly withdrawal rises with inflation each year. Returns how
// many months the corpus lasts (null if it outlasts maxYears).
export function swpWithInflation({ corpus, annualReturn, withdrawal, inflation, maxYears = 50 }) {
  const i = annualReturn / 1200;
  let c = corpus;
  let w = withdrawal;
  let withdrawn = 0;
  const series = [];
  for (let m = 1; m <= maxYears * 12; m++) {
    c = c * (1 + i) - w;
    if (c <= 0) {
      withdrawn += w + c;
      series.push({ year: +(m / 12).toFixed(1), value: 0, invested: Math.round(withdrawn) });
      return { months: m, withdrawn, series, finalCorpus: 0 };
    }
    withdrawn += w;
    if (m % 12 === 0) {
      series.push({ year: m / 12, value: Math.round(c), invested: Math.round(withdrawn) });
      w *= 1 + inflation / 100;
    }
  }
  return { months: null, withdrawn, series, finalCorpus: c };
}

// Business treasury: estimated yearly earnings on an idle balance at different
// pre-tax rates, plus value after `years` with annual compounding.
export function idleCashEarnings({ balance, ratePct, years = 1 }) {
  const yearly = (balance * ratePct) / 100;
  return { yearly, afterYears: balance * Math.pow(1 + ratePct / 100, years) - balance };
}
